/**
 * Small display helpers shared across the attendance views, exposed as a Vue 2
 * mixin so templates can call them directly (ported from the source app's
 * `lib/format.ts`). Bootstrap badge classes replace PrimeVue Tag severities.
 */

const STATUS_LABELS = {
	present: 'Present',
	absent: 'Absent',
	half_day: 'Half day',
	weekly_off: 'Weekly off',
	holiday: 'Holiday',
	on_leave: 'On leave',
	lwp: 'LWP',
	wfh: 'Work from home',
	on_duty: 'On duty',
	comp_off: 'Comp off',
	missing_punch: 'Missing punch',
	not_computed: 'Not computed',
}

const MODULE_LABELS = {
	leave: 'Leave',
	overtime: 'Overtime',
	regularization: 'Correction',
	short_leave: 'Short leave',
	geo_exception: 'Out of area',
	deduction: 'Deduction',
}

export default {
	methods: {
		/** 508 -> "8h 28m". Minutes are the engine's unit everywhere. */
		minutesToHours(minutes) {
			if (!minutes) return '—'
			const h = Math.floor(minutes / 60)
			const m = minutes % 60
			if (h === 0) return `${m}m`
			return m === 0 ? `${h}h` : `${h}h ${m}m`
		},

		formatDate(value) {
			if (!value) return '—'
			const date = new Date(value)
			return Number.isNaN(date.getTime())
				? value
				: date.toLocaleDateString(undefined, {
						day: '2-digit',
						month: 'short',
						year: 'numeric',
				  })
		},

		formatDateTime(value) {
			if (!value) return '—'
			const date = new Date(String(value).replace(' ', 'T'))
			return Number.isNaN(date.getTime())
				? value
				: date.toLocaleString(undefined, {
						day: '2-digit',
						month: 'short',
						hour: '2-digit',
						minute: '2-digit',
				  })
		},

		/** YYYY-MM-DD in local time — the API's date format, without a UTC shift. */
		toIsoDate(date) {
			const offset = date.getTimezoneOffset() * 60000
			return new Date(date.getTime() - offset).toISOString().slice(0, 10)
		},

		currentMonth() {
			return this.toIsoDate(new Date()).slice(0, 7)
		},

		statusLabel(status) {
			return STATUS_LABELS[status] || status
		},

		/** Bootstrap badge class per status, so colour meaning stays consistent. */
		statusBadgeClass(status) {
			switch (status) {
				case 'present':
				case 'wfh':
				case 'on_duty':
					return 'badge bg-success'
				case 'half_day':
				case 'missing_punch':
					return 'badge bg-warning text-dark'
				case 'absent':
				case 'lwp':
					return 'badge bg-danger'
				case 'on_leave':
					return 'badge bg-info text-dark'
				case 'weekly_off':
				case 'holiday':
					return 'badge bg-secondary'
				default:
					return 'badge bg-dark'
			}
		},

		/**
		 * A deduction's value carries its own unit, so 200 shows as an amount,
		 * 0.5 as half a day and 30 as minutes. Credits are signed the other way.
		 */
		deductionLabel(row) {
			if (!row) return ''
			const sign = row.direction === 'credit' ? '+' : '-'
			const value = Number(row.value || 0)
			if (row.unit === 'days') return `${sign}${value} d`
			if (row.unit === 'hours') return `${sign}${this.minutesToHours(value * 60)}`
			return `${sign}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
		},

		moduleLabel(module) {
			return MODULE_LABELS[module] || module
		},
	},
}
